"use client";

import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./TourOverlay.module.css";

/**
 * Full-screen dark overlay with a spotlight cutout around a target element.
 *
 * The cutout is achieved with a massive box-shadow on a positioned spotlight div.
 * For interactive steps, clicks pass through the cutout to the page beneath.
 * For non-interactive steps, a click-blocker prevents interaction with the page.
 *
 * When the target element hasn't appeared in the DOM yet, a plain dark overlay
 * is rendered as a fallback until the element is found.
 */
export default function TourOverlay({ targetSelector, interactive = false, padding = 12, borderRadius = 12 }) {
  const [rect, setRect] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  // Reset rect when target changes so we don't show stale spotlight
  useEffect(() => {
    setRect(null);
  }, [targetSelector]);

  const measure = useCallback(() => {
    if (!targetSelector) return;
    const el = document.querySelector(targetSelector);
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - padding,
      left: r.left - padding,
      width: r.width + padding * 2,
      height: r.height + padding * 2,
    });
  }, [targetSelector, padding]);

  useEffect(() => {
    measure();

    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      observer.disconnect();
    };
  }, [measure]);

  if (!portalTarget) return null;

  return createPortal(
    <div className={styles.overlay}>
      {!interactive && <div className={styles.clickBlocker} />}

      {rect ? (
        <div
          className={styles.spotlight}
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius,
          }}
        />
      ) : (
        <div className={styles.darkFallback} />
      )}
    </div>,
    portalTarget,
  );
}
